import { clampTo, extractImageFrames } from '../../tools';
import type { WavespeedModelConfig } from '../types';

export const googleConfigs: Record<string, WavespeedModelConfig> = {
    'gemini-omni-flash-api': {
        // https://wavespeed.ai/zh-CN/models/google/gemini-omni-flash/text-to-video
        // https://wavespeed.ai/zh-CN/models/google/gemini-omni-flash/image-to-video
        // https://wavespeed.ai/zh-CN/models/google/gemini-omni-flash/reference-to-video
        // https://wavespeed.ai/zh-CN/models/google/gemini-omni-flash/video-edit
        apiPath: (params) => {
            const { frameMode} = params;
            if (frameMode === 'first') {
                return '/google/gemini-omni-flash/image-to-video';
            } else if (frameMode === 'ref' ) {
                return '/google/gemini-omni-flash/reference-to-video';
            } else if (frameMode === 'v2v') {
                return '/google/gemini-omni-flash/video-edit';
            }
            return '/google/gemini-omni-flash/text-to-video';
        },
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, imagePrompt, videoUri, aspectRatio, duration, modelUi } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage] = extractImageFrames(imagePrompt);
            if (frameMode === 'first') {
                return {
                    prompt: prompt,
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['16:9', '9:16'] as const, '16:9'),
                    duration: clampTo(duration || 8, [3, 4, 5, 6, 7, 8, 9, 10] as const, 8),
                    // 图片 URL
                    image: firstImage,
                };
            } else if (frameMode === 'ref') {
                return {
                    prompt: prompt,
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['16:9', '9:16'] as const, '16:9'),
                    duration: clampTo(duration || 8, [3, 4, 5, 6, 7, 8, 9, 10] as const, 8),
                    // 图片 URL
                    images: imagePrompt!.slice(0, 4),
                };
            } else if (frameMode === 'v2v') {
                return {
                    prompt: prompt,
                    video: videoUri,
                };
            } else {
                return {
                    prompt: prompt.slice(0, 20000),
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['16:9', '9:16'] as const, '16:9'),
                    duration: clampTo(duration || 8, [3, 4, 5, 6, 7, 8, 9, 10] as const, 8),
                };
            }
        },
    },
    'gemini-omni-1.1-flash': {
        // https://wavespeed.ai/zh-CN/models/google/gemini-omni-1.1-flash/text-to-video
        // https://wavespeed.ai/zh-CN/models/google/gemini-omni-1.1-flash/image-to-video
        // https://wavespeed.ai/zh-CN/models/google/gemini-omni-1.1-flash/reference-to-video
        // https://wavespeed.ai/zh-CN/models/google/gemini-omni-1.1-flash/video-edit
        apiPath: (params) => {
            const { frameMode } = params;
            if (frameMode === 'first' || frameMode === 'first_last') {
                return '/google/gemini-omni-1.1-flash/image-to-video';
            } else if (frameMode === 'ref') {
                return '/google/gemini-omni-1.1-flash/reference-to-video';
            } else if (frameMode === 'v2v') {
                return '/google/gemini-omni-1.1-flash/video-edit';
            }
            return '/google/gemini-omni-1.1-flash/text-to-video';
        },
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, imagePrompt, videoUri, videoUris, aspectRatio, duration, quality, modelUi } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage, lastImage] = extractImageFrames(imagePrompt);
            const images = (imagePrompt ?? []).filter(Boolean).slice(0, 10);
            const videos = (
                (videoUris?.length ?? 0) > 0 ? videoUris! : videoUri ? [videoUri] : []
            ).filter(Boolean).slice(0, 3);
            if (frameMode === 'first') {
                return {
                    prompt: prompt,
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['16:9', '9:16'] as const, '16:9'),
                    duration: clampTo(duration || 8, [3, 4, 5, 6, 7, 8, 9, 10] as const, 8),
                    resolution: clampTo((quality || '720p').toLowerCase(), ['360p', '720p', '1080p', '4k'] as const, '720p'),
                    // 首帧 URL
                    image: firstImage,
                };
            } else if (frameMode === 'first_last') {
                return {
                    prompt: prompt,
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['16:9', '9:16'] as const, '16:9'),
                    duration: clampTo(duration || 8, [3, 4, 5, 6, 7, 8, 9, 10] as const, 8),
                    resolution: clampTo((quality || '720p').toLowerCase(), ['360p', '720p', '1080p', '4k'] as const, '720p'),
                    // 首帧 URL
                    image: firstImage,
                    // 尾帧 URL
                    last_image: lastImage,
                };
            } else if (frameMode === 'ref') {
                return {
                    prompt: prompt,
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['16:9', '9:16'] as const, '16:9'),
                    duration: clampTo(duration || 8, [3, 4, 5, 6, 7, 8, 9, 10] as const, 8),
                    resolution: clampTo((quality || '720p').toLowerCase(), ['360p', '720p', '1080p', '4k'] as const, '720p'),
                    // 参考图 URL（最多 10 张）
                    ...(images.length ? { images } : {}),
                    // 参考视频 URL（最多 3 条，每条不超过 3 秒）
                    ...(videos.length ? { reference_videos: videos } : {}),
                };
            } else if (frameMode === 'v2v') {
                return {
                    prompt: prompt,
                    resolution: clampTo((quality || '720p').toLowerCase(), ['360p', '720p', '1080p', '4k'] as const, '720p'),
                    // 视频 URL
                    video: videoUri,
                };
            } else {
                return {
                    prompt: prompt.slice(0, 20000),
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['16:9', '9:16'] as const, '16:9'),
                    duration: clampTo(duration || 8, [3, 4, 5, 6, 7, 8, 9, 10] as const, 8),
                    resolution: clampTo((quality || '720p').toLowerCase(), ['360p', '720p', '1080p', '4k'] as const, '720p'),
                };
            }
        },
    },
};